import React, { useState, useEffect } from 'react';
import { auth, database, functions } from './firebase';
import { ref, set, get } from 'firebase/database';
import { httpsCallable } from 'firebase/functions';
import { useImpersonation } from './ImpersonationContext';

const VERIFY_TTL_DAYS = 90;

function daysSince(isoString) {
  if (!isoString) return Infinity;
  return (Date.now() - new Date(isoString).getTime()) / (1000 * 60 * 60 * 24);
}

function CueScoreLink({ onLinked }) {
  const [cuescoreUsername, setCuescoreUsername] = useState('');
  const [cuescoreEmail, setCuescoreEmail] = useState('');
  const [cuescorePassword, setCuescorePassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentUsername, setCurrentUsername] = useState('');
  const [hasCredentials, setHasCredentials] = useState(false);
  const [verifiedAt, setVerifiedAt] = useState(null);
  const [isVerified, setIsVerified] = useState(false);

  const { effectiveUserId, isImpersonating } = useImpersonation();

  useEffect(() => {
    // Reset all form state before loading new user's data to prevent leakage between accounts
    setCuescoreUsername('');
    setCuescoreEmail('');
    setCuescorePassword('');
    setCurrentUsername('');
    setHasCredentials(false);
    setIsVerified(false);
    setVerifiedAt(null);
    setError('');
    setSuccess('');

    const loadData = async () => {
      const user = auth.currentUser;
      if (!user || !effectiveUserId) return;

      const snap = await get(ref(database, `organisers/${effectiveUserId}`));
      if (snap.exists()) {
        const val = snap.val();
        if (val.cuescoreUsername) {
          setCurrentUsername(val.cuescoreUsername);
          setCuescoreUsername(val.cuescoreUsername);
        }
        setIsVerified(!!val.cuescoreVerified);
        if (val.cuescoreVerifiedAt) setVerifiedAt(val.cuescoreVerifiedAt);
      }

      const credsSnap = await get(ref(database, `organisers/${effectiveUserId}/cuescoreCredentials`));
      if (credsSnap.exists()) {
        setHasCredentials(true);
        const creds = credsSnap.val();
        if (creds.email) setCuescoreEmail(creds.email);
      }
    };

    loadData();
  }, [effectiveUserId]);

  const handleSave = async () => {
    if (isImpersonating) return;

    if (!cuescoreUsername.trim()) {
      setError('Please enter your CueScore public username');
      return;
    }
    if (!cuescoreEmail.trim()) {
      setError('Please enter your CueScore login email');
      return;
    }
    if (!cuescorePassword.trim()) {
      setError('CueScore password is required to verify account ownership and enable notifications');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');

      // Verify CueScore ownership — sets cuescoreVerified + cuescoreVerifiedAt in RTDB
      const verifyCueScoreOwnership = httpsCallable(functions, 'verifyCueScoreOwnership');
      await verifyCueScoreOwnership({
        username: cuescoreEmail.trim(),
        password: cuescorePassword.trim(),
        publicUsername: cuescoreUsername.trim(),
      });

      // Encrypt and store credentials for venue sync
      const encryptCuescorePassword = httpsCallable(functions, 'encryptCuescorePassword');
      const encryptResult = await encryptCuescorePassword({ password: cuescorePassword.trim() });
      if (!encryptResult.data?.encryptedPassword) {
        throw new Error('Failed to encrypt password');
      }

      await set(ref(database, `organisers/${effectiveUserId}/cuescoreCredentials`), {
        username: cuescoreEmail.trim(),
        email: cuescoreEmail.trim(),
        encryptedPassword: encryptResult.data.encryptedPassword,
        passwordSet: true,
        lastUpdated: new Date().toISOString(),
      });

      setHasCredentials(true);
      setCurrentUsername(cuescoreUsername.trim());
      setIsVerified(true);
      setVerifiedAt(new Date().toISOString());
      setCuescorePassword('');

      setSuccess('CueScore account verified and linked. Notifications are now enabled.');

      if (onLinked) onLinked(cuescoreUsername.trim());
    } catch (err) {
      const msg = err?.message || 'Failed to link account. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const verifyAge = daysSince(verifiedAt);
  const verifyExpired = isVerified && verifyAge > VERIFY_TTL_DAYS;
  const verifyExpiringSoon = isVerified && !verifyExpired && verifyAge > VERIFY_TTL_DAYS - 14;
  const daysRemaining = Math.max(0, Math.ceil(VERIFY_TTL_DAYS - verifyAge));

  const styles = {
    card: {
      backgroundColor: '#1f2937',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '24px',
    },
    title: { fontSize: '20px', fontWeight: 'bold', marginBottom: '16px' },
    banner: (bg, border, color) => ({
      marginBottom: '16px',
      padding: '12px 16px',
      backgroundColor: bg,
      border: `1px solid ${border}`,
      borderRadius: '8px',
      fontSize: '14px',
      color,
      lineHeight: '1.5',
    }),
    inputGroup: { marginBottom: '16px' },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '500',
      color: '#d1d5db',
      marginBottom: '8px',
    },
    required: { color: '#f87171', marginLeft: '4px' },
    input: {
      width: '100%',
      padding: '12px 16px',
      backgroundColor: '#374151',
      color: 'white',
      border: '2px solid #4b5563',
      borderRadius: '8px',
      fontSize: '16px',
      outline: 'none',
      boxSizing: 'border-box',
    },
    passwordWrapper: { position: 'relative' },
    eyeButton: {
      position: 'absolute',
      right: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'none',
      border: 'none',
      color: '#9ca3af',
      cursor: 'pointer',
      fontSize: '14px',
    },
    hint: { fontSize: '12px', color: '#9ca3af', marginTop: '4px', lineHeight: '1.5' },
    button: {
      padding: '12px 24px',
      backgroundColor: '#2563eb',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: 'bold',
      cursor: 'pointer',
    },
    buttonDisabled: {
      padding: '12px 24px',
      backgroundColor: '#4b5563',
      color: '#9ca3af',
      border: 'none',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: 'bold',
      cursor: 'not-allowed',
    },
    error: {
      marginTop: '12px',
      padding: '12px',
      backgroundColor: '#7f1d1d',
      color: '#fca5a5',
      borderRadius: '8px',
      fontSize: '14px',
    },
    success: {
      marginTop: '12px',
      padding: '12px',
      backgroundColor: '#14532d',
      color: '#86efac',
      borderRadius: '8px',
      fontSize: '14px',
    },
  };

  return (
    <div style={styles.card}>
      <h2 style={styles.title}>🔗 Link CueScore Account</h2>

      {/* Impersonation read-only banner */}
      {isImpersonating && (
        <div style={styles.banner('rgba(245,158,11,0.12)', 'rgba(245,158,11,0.4)', '#f59e0b')}>
          🎭 <strong>Viewing as impersonated user</strong> — CueScore settings are read-only.
          Stop impersonating to make changes.
        </div>
      )}

      {/* Verification status banners */}
      {verifyExpired && (
        <div style={styles.banner('rgba(239,68,68,0.12)', 'rgba(239,68,68,0.4)', '#f87171')}>
          🔐 <strong>Verification expired</strong> — re-enter your password below to re-verify and
          re-enable notifications.
        </div>
      )}
      {verifyExpiringSoon && (
        <div style={styles.banner('rgba(245,158,11,0.10)', 'rgba(245,158,11,0.35)', '#f59e0b')}>
          ⚠️ Verification expires in <strong>{daysRemaining} days</strong> — re-enter your
          password to renew.
        </div>
      )}
      {isVerified && !verifyExpired && !verifyExpiringSoon && currentUsername && (
        <div style={styles.banner('rgba(16,185,129,0.10)', 'rgba(16,185,129,0.35)', '#6ee7b7')}>
          ✓ Verified as <strong>{currentUsername}</strong> — notifications enabled · expires in{' '}
          {daysRemaining} days
        </div>
      )}
      {!isVerified && currentUsername && (
        <div style={styles.banner('rgba(239,68,68,0.12)', 'rgba(239,68,68,0.4)', '#f87171')}>
          🔐 <strong>Not yet verified</strong> — enter your password below to verify ownership and
          enable notifications.
        </div>
      )}

      {/* Public username */}
      <div style={styles.inputGroup}>
        <label style={styles.label}>
          CueScore Public Username<span style={styles.required}>*</span>
        </label>
        <input
          type="text"
          value={cuescoreUsername}
          onChange={(e) => setCuescoreUsername(e.target.value)}
          placeholder="e.g., rgpick"
          style={styles.input}
          disabled={loading}
        />
        <div style={styles.hint}>
          Your profile name from the URL: cuescore.com/<strong>{cuescoreUsername || 'username'}</strong>/tournaments
        </div>
      </div>

      {/* Login email */}
      <div style={styles.inputGroup}>
        <label style={styles.label}>
          CueScore Login Email<span style={styles.required}>*</span>
        </label>
        <input
          type="email"
          value={cuescoreEmail}
          onChange={(e) => setCuescoreEmail(e.target.value)}
          placeholder="your@email.com"
          style={styles.input}
          disabled={loading}
        />
        <div style={styles.hint}>
          The email you use to log in to CueScore (may differ from your public username)
        </div>
      </div>

      {/* Password */}
      <div style={styles.inputGroup}>
        <label style={styles.label}>
          CueScore Password<span style={styles.required}>*</span>
        </label>
        <div style={styles.passwordWrapper}>
          <input
            type={showPassword ? 'text' : 'password'}
            value={cuescorePassword}
            onChange={(e) => setCuescorePassword(e.target.value)}
            placeholder={hasCredentials ? 'Enter password to update / re-verify' : 'Your CueScore password'}
            style={styles.input}
            disabled={loading}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            style={styles.eyeButton}
          >
            {showPassword ? '🙈' : '👁️'}
          </button>
        </div>
        <div style={styles.hint}>
          Used to verify you own this CueScore account. Encrypted before storage — never sent in plain text.
          Required to enable player notifications.
        </div>
      </div>

      <button
        onClick={handleSave}
        style={loading || isImpersonating ? styles.buttonDisabled : styles.button}
        disabled={loading || isImpersonating}
      >
        {loading
          ? '🔐 Verifying…'
          : currentUsername
          ? verifyExpired
            ? 'Re-verify & Update'
            : 'Update'
          : 'Verify & Save'}
      </button>

      {error && <div style={styles.error}>⚠️ {error}</div>}
      {success && <div style={styles.success}>✓ {success}</div>}
    </div>
  );
}

export default CueScoreLink;
